import { DeliverrAddress } from "@deliverr/commons-objects";
import { AttachmentType } from "@deliverr/replenishment-client";
import { PrepOption } from "@deliverr/business-types";
import { Step } from "common/components/StepContainer";
import { transferCreateSelectDestinationAddress, transferCreateSetDestinationWarehouseId } from "./actions";
import { updateShipmentExternalIds } from "./actions/replenishmentOrder/updateShipmentExternalIds";
import { AmazonPickFacilityStep } from "./steps/common/replenishment/AmazonPickFacilityStep";
import { ReplenishmentOrderShippingStep } from "./steps/common/replenishment/ReplenishmentOrderShippingStep";
import { ReplenishmentOrderConfirmation } from "./steps/common/replenishment/confirm/ReplenishmentOrderConfirmation";
import { ReplenishmentShippingDetailsStep } from "./steps/common/replenishment/shipment-details/ReplenishmentShippingDetailsStep";
import { selectTransferCreate } from "./store/TransferCreateSelectors";
import { addLoader, clearLoader } from "common/components/WithLoader/LoadingActions";
import { CreateTransferLoader } from "./CreateTransferLoader.types";
import { TransferSelectPrepTypeStep } from "prep/create/ReplenishmentPrep/TransferSelectPrepTypeStep";
import { TransferAddAmazonBarcodesForUnitPrep } from "prep/create/ReplenishmentPrep/TransferAddAmazonBarcodesForUnitPrep";
import { isFNSKUOrASINBarcode } from "transfers/utils/isFNSKUBarcode";
import { SharedConfirmedStep } from "../../common/wholesale-common/components/SharedConfirmedStep";
import { transfersShipmentCreateLabels } from "./transfersShipmentCreate.labels";
import { withNextLoader } from "./steps/common/replenishment/utils";
import { TransferUnifiedProductSelectionStep } from "./steps/common/TransferUnifiedProductSelectionStep";
import { saveDraftReplenishmentOrder } from "./steps/common/actions/saveDraftReplenishmentOrder";
import { confirmDraftReplenishmentOrder } from "./steps/common/actions/confirmDraftReplenishmentOrder";
import { includes } from "lodash";

export enum FbaManualSteps {
  SelectAmazonFacility = "fbaManual/selectAmazonFacility",
  SelectProductAndQuantities = "fbaManual/selectProductAndQuantities",
  SelectPrepType = "fbaManual/selectPrepType",
  SelectShippingMethod = "fbaManual/selectShippingMethod",
  AddAmazonMissingBarcodesUnitPrep = "fbaManual/addAmazonMissingBarcodesUnitPrep",
  ShippingDetails = "fbaManual/shippingDetails",
  Confirm = "fbaManual/confirm",
  Confirmed = "fbaManual/confirmed",
}

export const fbaManualSteps: (
  dispatch,
  getState,
  isFbaUnitPrepOn: boolean,
  isFbaV3On: boolean
) => Record<FbaManualSteps, Step> = (dispatch, getState, isFbaUnitPrepOn) => ({
  [FbaManualSteps.SelectAmazonFacility]: {
    component: AmazonPickFacilityStep,
    async next(address: DeliverrAddress) {
      dispatch(transferCreateSelectDestinationAddress(address));
      dispatch(transferCreateSetDestinationWarehouseId(address.name));
      return FbaManualSteps.SelectProductAndQuantities;
    },
  },
  [FbaManualSteps.SelectProductAndQuantities]: {
    component: TransferUnifiedProductSelectionStep,
    next: withNextLoader(
      async () => {
        if (await dispatch(saveDraftReplenishmentOrder())) {
          return FbaManualSteps.SelectPrepType;
        }
        return;
      },
      dispatch,
      getState
    ),
  },
  [FbaManualSteps.SelectPrepType]: {
    component: TransferSelectPrepTypeStep,
    next: withNextLoader(
      async () => {
        const { selectedProducts, prepOptions } = selectTransferCreate(getState());
        if (await dispatch(saveDraftReplenishmentOrder())) {
          return includes(prepOptions, PrepOption.UNIT_PREP) &&
            Object.values(selectedProducts).some(({ barcodes }) => !barcodes?.find(isFNSKUOrASINBarcode))
            ? FbaManualSteps.AddAmazonMissingBarcodesUnitPrep
            : FbaManualSteps.SelectShippingMethod;
        }
        return;
      },
      dispatch,
      getState
    ),
  },
  [FbaManualSteps.AddAmazonMissingBarcodesUnitPrep]: {
    component: TransferAddAmazonBarcodesForUnitPrep,
    next: async () => {
      return FbaManualSteps.SelectShippingMethod;
    },
  },
  [FbaManualSteps.SelectShippingMethod]: {
    component: ReplenishmentOrderShippingStep,
    async next() {
      return FbaManualSteps.ShippingDetails;
    },
  },
  [FbaManualSteps.ShippingDetails]: {
    component: ReplenishmentShippingDetailsStep,
    async next(value: { attachmentTypes: AttachmentType[] }) {
      dispatch(addLoader(CreateTransferLoader.CreateTransferOrderLoader));
      const { replenishmentOrder } = selectTransferCreate(getState());
      const shipmentId = replenishmentOrder?.shipments?.[0]?.shipmentId ?? "";

      await dispatch(updateShipmentExternalIds(shipmentId, value.attachmentTypes));
      dispatch(clearLoader(CreateTransferLoader.CreateTransferOrderLoader));
      return FbaManualSteps.Confirm;
    },
  },
  [FbaManualSteps.Confirm]: {
    component: ReplenishmentOrderConfirmation,
    next: withNextLoader(
      async () => {
        if (await dispatch(confirmDraftReplenishmentOrder())) {
          return FbaManualSteps.Confirmed;
        }
        return undefined;
      },
      dispatch,
      getState
    ),
  },
  [FbaManualSteps.Confirmed]: {
    component: SharedConfirmedStep(transfersShipmentCreateLabels),
    hasNoBack: true,
  },
});
